import { createTheme } from "@mui/material/styles";

const appTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#000000",
      light: "#272727",
      dark: "#000000",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#b00020",
      contrastText: "#ffffff",
    },
    background: {
      default: "#f5f5f5",
      paper: "#ffffff",
    },
    text: {
      primary: "#111111",
      secondary: "#6b7280",
    },
    divider: "#e5e7eb",
  },

  shape: {
    borderRadius: 10,
  },

  typography: {
    fontFamily: '"Inter", "Roboto", "Arial", sans-serif',
    fontSize: 13,
    button: {
      textTransform: "none",
      fontWeight: 700,
    },
    h1: {
      fontWeight: 800,
    },
    h2: {
      fontWeight: 800,
    },
    h3: {
      fontWeight: 800,
    },
    h4: {
      fontWeight: 800,
    },
    h5: {
      fontWeight: 800,
    },
    h6: {
      fontWeight: 800,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          boxSizing: "border-box",
        },
        html: {
          minHeight: "100%",
        },
        body: {
          minHeight: "100%",
          margin: 0,
          backgroundColor: "#f5f5f5",
          color: "#111111",
        },
        "#root": {
          minHeight: "100vh",
        },
      },
    },

    MuiButton: {
      defaultProps: {
        size: "small",
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minHeight: 38,
          borderRadius: 10,
          paddingLeft: 18,
          paddingRight: 18,
          fontSize: 13,
          fontWeight: 700,
          textTransform: "none",
        },
        containedPrimary: {
          backgroundColor: "#000000",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#272727",
          },
          "&.Mui-disabled": {
            backgroundColor: "#d1d5db",
            color: "#ffffff",
          },
        },
        outlinedPrimary: {
          borderColor: "#000000",
          color: "#000000",
          "&:hover": {
            borderColor: "#000000",
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        size: "small",
        variant: "outlined",
      },
    },

    MuiFormControl: {
      defaultProps: {
        size: "small",
        variant: "outlined",
      },
    },

    MuiSelect: {
      defaultProps: {
        size: "small",
        variant: "outlined",
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 10,
          backgroundColor: "#ffffff",
          fontSize: 13,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d1d5db",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#6b7280",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000000",
            borderWidth: 1.5,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d32f2f",
          },
        },
        input: {
          paddingTop: 10,
          paddingBottom: 10,
          fontSize: 13,
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 13,
          color: "#4b5563",
          "&.Mui-focused": {
            color: "#000000",
          },
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 13,
          fontWeight: 600,
          color: "#374151",
          "&.Mui-focused": {
            color: "#000000",
          },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 2,
          fontSize: 11,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 36,
          borderRadius: 8,
          margin: "2px 6px",
          fontSize: 13,
          "&.Mui-selected": {
            backgroundColor: "rgba(0, 0, 0, 0.08)",
          },
          "&.Mui-selected:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.12)",
          },
        },
      },
    },

    MuiMenu: {
      defaultProps: {
        PaperProps: {
          elevation: 3,
        },
      },
      styleOverrides: {
        paper: {
          marginTop: 4,
          borderRadius: 10,
          border: "1px solid #e5e7eb",
        },
        list: {
          paddingTop: 6,
          paddingBottom: 6,
        },
      },
    },

    MuiAutocomplete: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: "1px solid #e5e7eb",
        },
        option: {
          minHeight: 36,
          margin: "2px 6px",
          borderRadius: 8,
          fontSize: 13,
        },
      },
    },

    MuiCheckbox: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          color: "#6b7280",
          "&.Mui-checked": {
            color: "#000000",
          },
        },
      },
    },

    MuiRadio: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          color: "#6b7280",
          "&.Mui-checked": {
            color: "#000000",
          },
        },
      },
    },

    MuiSwitch: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: "#000000",
          },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#000000",
            opacity: 1,
          },
        },
      },
    },

    MuiIconButton: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          borderRadius: 9,
          color: "#111111",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.06)",
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: {
          borderRadius: 12,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: "#e5e7eb",
          fontSize: 13,
        },
        head: {
          backgroundColor: "#f9fafb",
          color: "#111111",
          fontWeight: 700,
        },
      },
    },

    MuiChip: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
        },
      },
    },

    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "&.Mui-selected": {
            backgroundColor: "#000000",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "#272727",
            },
          },
        },
      },
    },
  },
});

export default appTheme;